/* eslint-disable @typescript-eslint/no-explicit-any */
import { Columns, Column } from '@bedrock-layout/primitives'
import {
  BrandButton,
  BrandFormSelectItem,
  Form,
  Heading,
  Paragraph,
  ReactHookBrandFormSelect,
} from '@okam/brand-ui/index'
import { ReactHookFormTextarea, ReactHookFormInput } from '@okam/shared-ui/index'
import axios from 'axios'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { useMutation } from 'react-query'
import { getRestUrl, getFormContact } from '@/config/index'

interface Form1Variables {
  input_1: string
  input_3: string
  input_10: string
  input_6: string
  input_7: string
  input_8: string
}

interface GFValidationMessages {
  [x: string]: string
}

interface GFFormResponse {
  is_valid: boolean
  page_number: number
  source_page_number: number
  confirmation_message?: string
  confirmation_type?: string
  validation_messages?: GFValidationMessages
}

const formSubmission = async (formId: string, variables: Form1Variables, locale: string | undefined) => {
  const wpJsonUrl = getRestUrl()
  if (!wpJsonUrl) {
    throw new Error('Bad form configuration')
  }

  const url = `${wpJsonUrl}/gf/v2/forms/${formId}/submissions${locale ? `?lang=${locale}` : ''}`

  try {
    const { data, status } = await axios.post(url, variables, {
      timeout: 5000,
    })

    if (status === 200 || status === 400) {
      return data as GFFormResponse
    }

    throw new Error('Error when submitting the form')
  } catch (e) {
    // nothing
  }
  throw new Error('Error when submitting the form')
}

interface ContactFormProps {
  locale?: string
}

export default function ContactForm({ locale }: ContactFormProps) {
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const formId = getFormContact()
  const { t } = useTranslation()

  const { mutate } = useMutation<GFFormResponse, Error, Form1Variables>({
    mutationFn: (variables: Form1Variables) => formSubmission(formId, variables, locale),
    onSuccess: (mutationData: GFFormResponse) => {
      if (!mutationData.is_valid || mutationData.validation_messages) {
        setErrorMessage('Error when sending form')
        return
      }

      setSuccessMessage('Form sent')
    },
    onError: (error: Error) => {
      setErrorMessage(error.message)
    },
  })

  const submitForm = (onSubmitData: any) => {
    setSuccessMessage('')
    setErrorMessage('')
    mutate(onSubmitData)
  }

  return (
    <div>
      <div tw="max-w-xl mx-auto">
        <Heading as="h2" variant="h2" tw="text-center pb-4">
          {t('FORM.CONTACT.TITLE')}
        </Heading>
        <Paragraph as="p" variant="p" tw="text-center pb-4">
          {t('FORM.CONTACT.DESCRIPTION')}
        </Paragraph>
      </div>

      {successMessage && (
        <div tw="text-center py-4">
          <Paragraph as="p" variant="p" tw="pb-4">
            {t('FORM.CONTACT.CONFIRMATION')}
          </Paragraph>
          <div tw="flex justify-center">
            <BrandButton as="a" buttonStyle="default" variant="primary" href={t('ROUTES.HOME') ?? ''}>
              {t('BUTTON.BACK_HOMEPAGE')}
            </BrandButton>
          </div>
        </div>
      )}

      {!successMessage && (
        <Form onSubmit={submitForm} tw="font-body">
          <Columns columns={2} gutter="size3" switchAt="834px" tw="p-4">
            <ReactHookFormInput
              id="input_1"
              name="input_1"
              ariaLabel={t('FORM.LABEL.FIRST_NAME') ?? ''}
              label={t('FORM.LABEL.FIRST_NAME') ?? ''}
              placeholder={t('FORM.LABEL.FIRST_NAME') ?? ''}
              required
            />
            <ReactHookFormInput
              id="input_3"
              name="input_3"
              ariaLabel={t('FORM.LABEL.LAST_NAME') ?? ''}
              label={t('FORM.LABEL.LAST_NAME') ?? ''}
              placeholder={t('FORM.LABEL.LAST_NAME') ?? ''}
              required
            />
            <ReactHookFormInput
              id="input_4"
              name="input_4"
              ariaLabel={t('FORM.LABEL.EMAIL') ?? ''}
              label={t('FORM.LABEL.EMAIL') ?? ''}
              placeholder={t('FORM.LABEL.EMAIL') ?? ''}
              type="email"
              validation={{
                pattern: {
                  value: /\S[^\s@]*@\S+\.\S+/,
                  message: t('FORM.ERRORS.EMAIL'),
                },
              }}
              required
            />
            <ReactHookBrandFormSelect label={t('FORM.LABEL.INTEREST') ?? ''} id="input_10" name="input_10">
              <BrandFormSelectItem
                value={t('FORM.LABEL.INTEREST_OPTION1') ?? ''}
                label={t('FORM.LABEL.INTEREST_OPTION1') ?? ''}
              />
              <BrandFormSelectItem
                value={t('FORM.LABEL.INTEREST_OPTION2') ?? ''}
                label={t('FORM.LABEL.INTEREST_OPTION2') ?? ''}
              />
              <BrandFormSelectItem
                value={t('FORM.LABEL.INTEREST_OPTION3') ?? ''}
                label={t('FORM.LABEL.INTEREST_OPTION3') ?? ''}
              />
            </ReactHookBrandFormSelect>
            <ReactHookFormInput
              id="input_6"
              name="input_6"
              ariaLabel={t('FORM.LABEL.UNIVERSITY') ?? ''}
              label={t('FORM.LABEL.UNIVERSITY') ?? ''}
              placeholder={t('FORM.LABEL.UNIVERSITY') ?? ''}
              required
            />
            <ReactHookFormInput
              id="input_7"
              name="input_7"
              ariaLabel={t('FORM.LABEL.EDUCATION_YEAR') ?? ''}
              label={t('FORM.LABEL.EDUCATION_YEAR') ?? ''}
              placeholder={t('FORM.LABEL.EDUCATION_YEAR') ?? ''}
              type="number"
              required
            />
            <Column span={2}>
              <ReactHookFormTextarea
                id="input_8"
                name="input_8"
                ariaLabel={t('FORM.LABEL.MESSAGE') ?? ''}
                label={t('FORM.LABEL.MESSAGE') ?? ''}
                placeholder={t('FORM.LABEL.MESSAGE') ?? ''}
                required
              />
            </Column>
          </Columns>

          {errorMessage && <div>{errorMessage}</div>}
          <div tw="flex justify-center pt-4">
            <BrandButton as="button" buttonStyle="default" variant="primary" type="submit">
              {t('FORM.LABEL.SEND')}
            </BrandButton>
          </div>
        </Form>
      )}
    </div>
  )
}
